/* eslint-disable react-refresh/only-export-components */
import { Link, LinkProps } from 'react-router';

import { useTranslation } from 'react-i18next';

import {
  Focusable,
  Surface,
  SurfaceProps,
  Tabs as TabsCore,
  TabsItemProps as TabsCoreItemProps,
  TabsListProps as TabsCoreListProps,
  TabsProps as TabsCoreProps,
  Tooltip,
} from '@cfa/react-core';
import { ChevronLeft } from '@cfa/system-icons';

import { cn } from '@/utils/tailwind';

import { TranslationKey } from '@/i18n';

interface TxProp {
  /**
   * The translation key of the back button.
   *
   * This is preferred over normal children text.
   */
  tx?: TranslationKey;
}

/* ------------------------------------------------------------ */

interface BackLinkProps extends LinkProps, TxProp {
  as?: 'link';
}
interface BackButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, TxProp {
  as: 'button';
}

function Back(props: BackLinkProps): React.ReactNode;
function Back(props: BackButtonProps): React.ReactNode;
function Back(props: BackLinkProps | BackButtonProps): React.ReactNode {
  const { t } = useTranslation();

  const className =
    'text-brand-navy! flex cursor-pointer items-center gap-1 truncate';
  const content = (
    <>
      <ChevronLeft />
      {props.tx ? t(props.tx) : props.children}
    </>
  );

  return props.as === 'button' ? (
    <button {...props} className={cn(className, props.className)}>
      {content}
    </button>
  ) : (
    <Link {...props} className={cn(className, props.className)}>
      {content}
    </Link>
  );
}

export type BackProps = BackLinkProps | BackButtonProps;

type BackElement = React.ReactElement<BackProps, typeof Back>;

/* ------------------------------------------------------------ */

export interface TitleProps
  extends React.HTMLAttributes<HTMLParagraphElement>, TxProp {}

const Title: React.FC<TitleProps> = ({ tx, children, ...props }) => {
  const { t } = useTranslation();

  return (
    <p
      className={cn(
        'text-grayscale-gray7 truncate pt-1 font-bold',
        props.className,
      )}
      {...props}
    >
      {tx ? t(tx) : children}
    </p>
  );
};

type TitleElement = React.ReactElement<TitleProps, typeof Title>;

/* ------------------------------------------------------------ */

type Size = 'sm' | 'md' | 'lg' | 'xl' | number;

interface ActionIconButtonCommonProps {
  /**
   * The icon of the action icon button.
   */
  icon?: React.ComponentType<
    { width?: number; height?: number } | { size?: Size }
  >;
  /** Text placed to the right of the icon. */
  text?: TranslationKey;
  /** Tooltip text. */
  tooltip?: TranslationKey;
}

interface ActionIconButtonAsButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    ActionIconButtonCommonProps,
    TxProp {
  as?: 'button';
}
interface ActionIconButtonAsLinkProps
  extends LinkProps, ActionIconButtonCommonProps, TxProp {
  as: 'link';
  disabled?: boolean;
}

function ActionIconButton(
  props: ActionIconButtonAsButtonProps,
): React.ReactNode;
function ActionIconButton(props: ActionIconButtonAsLinkProps): React.ReactNode;
function ActionIconButton(
  props: ActionIconButtonAsButtonProps | ActionIconButtonAsLinkProps,
): React.ReactNode {
  const { icon, text, disabled = false, tooltip, children, ...rest } = props;
  const { t } = useTranslation();
  const IconComponent = icon;

  const content = (
    <>
      {IconComponent ? (
        <IconComponent size={24} width={24} height={24} />
      ) : (
        children
      )}
      {text && <p className="truncate">{t(text)}</p>}
    </>
  );

  const className = cn(
    'flex items-center gap-2 transition-colors',
    disabled
      ? 'text-disabled-contrast! cursor-not-allowed'
      : 'text-secondary-base! cursor-pointer',
    rest.className,
  );

  const mainButton =
    rest.as === 'link' ? (
      <Link
        {...rest}
        to={disabled ? '/' : rest.to}
        className={className}
        data-testid={`action-icon-button-${IconComponent?.displayName ?? text}`}
        onClick={e => {
          if (disabled) {
            e.preventDefault();
          } else {
            rest.onClick?.(e);
          }
        }}
      >
        {content}
      </Link>
    ) : (
      <button
        className={className}
        disabled={disabled}
        {...rest}
        data-testid={`action-icon-button-${IconComponent?.displayName ?? text}`}
      >
        {content}
      </button>
    );

  return (
    <Tooltip.Root isDisabled={!tooltip} delay={500}>
      <Focusable>{mainButton}</Focusable>
      <Tooltip.Content>{tooltip ? t(tooltip) : ''}</Tooltip.Content>
    </Tooltip.Root>
  );
}

export type ActionIconButtonProps =
  | ActionIconButtonAsLinkProps
  | ActionIconButtonAsButtonProps;

type ActionIconButtonElement = React.ReactElement<
  ActionIconButtonProps,
  typeof ActionIconButton
>;

/* ------------------------------------------------------------ */

type TabChildElement = TabElement | undefined | false;

interface TabsRootProps extends Omit<
  TabsCoreProps,
  'onSelectionChange' | 'selectedKey'
> {
  children?: TabChildElement | TabChildElement[];
  listProps?: TabsCoreListProps;
  selectedKey?: string;
  onSelectionChange?: (key: string) => void;
}

export type TabsProps = TabsRootProps;

const TabsRoot: React.FC<TabsRootProps> = ({
  children,
  listProps,
  onSelectionChange,
  selectedKey,
  ...rest
}) => (
  <TabsCore.Root
    color="secondary"
    {...rest}
    selectedKey={selectedKey}
    onSelectionChange={key => onSelectionChange?.(key as string)}
    className={cn(
      'max-md:shadow-elevation-1 items-center! max-md:fixed max-md:z-10 max-md:w-full',
      // List styles
      "max-md:[&>div[role='tablist']]:flex max-md:[&>div[role='tablist']]:w-full",
      // Tab styles
      "max-md:[&_div[role='tab']]:flex-1 max-md:[&_div[role='tab']]:pt-2 max-md:[&_div[role='tab']_div]:px-2!",
      rest.className,
    )}
  >
    <TabsCore.List {...listProps}>{children}</TabsCore.List>
  </TabsCore.Root>
);

type TabsRootElement = React.ReactElement<TabsRootProps, typeof TabsRoot>;

/* ------------------------------------------------------------ */

export interface TabProps extends Omit<TabsCoreItemProps, 'children'>, TxProp {
  children?: React.ReactNode;
}

const Tab: React.FC<TabProps> = ({ tx, children, ...props }) => {
  const { t } = useTranslation();
  return (
    <TabsCore.Item
      {...props}
      className={cn('cursor-pointer!', props.className)}
    >
      {tx ? t(tx) : children}
    </TabsCore.Item>
  );
};

type TabElement = React.ReactElement<TabProps, typeof Tab>;

/* ------------------------------------------------------------ */

type HeaderElement =
  | TitleElement
  | BackElement
  | ActionIconButtonElement
  | TabsRootElement
  | undefined
  | false
  | '';

export interface RootProps extends SurfaceProps {
  children?: HeaderElement | HeaderElement[];
}

const Root: React.FC<RootProps> = ({ children, ...props }) => {
  const actionIconButtonChildren = Array.isArray(children)
    ? children.filter(child => child && child.type === ActionIconButton)
    : [];
  const childrenAsArray = Array.isArray(children)
    ? children
    : children
      ? [children]
      : [];

  const topHeaderContent = (
    <>
      {
        <div className="flex items-center justify-start pt-1">
          {childrenAsArray.find(child => child && child.type === Back)}
        </div>
      }
      {childrenAsArray.find(child => child && child.type === Title) ?? <div />}
      {
        <div className="flex items-center justify-end gap-4">
          {actionIconButtonChildren}
        </div>
      }
    </>
  );

  const tabsComponent = childrenAsArray.find(
    child => child && child.type === TabsRoot,
  );

  const topHeaderClassName =
    'grid h-header-height grid-cols-[1fr_auto_1fr] items-center gap-4 max-md:hidden';

  return (
    <Surface
      data-testid="PageHeader"
      elevation={1}
      {...props}
      style={{
        top: 'var(--web-alert-banner-height, 0px)',
        ...props.style,
      }}
      className={cn(
        'bg-base-0 fixed z-6 rounded-none! md:right-0 md:left-[56px] md:pr-8 md:pl-6 lg:left-(--sidenav-width)',
        !tabsComponent && topHeaderClassName,
        props.className,
      )}
    >
      {tabsComponent ? (
        <div className={topHeaderClassName}>{topHeaderContent}</div>
      ) : (
        topHeaderContent
      )}
      {tabsComponent && tabsComponent}
    </Surface>
  );
};

interface Tabs {
  /**
   * The root tabs component.
   *
   * This serves as the parent component to compose the tabs.
   *
   * @example
   * <Tabs.Root>
   *   <Tabs.Tab id="tab1">Tab 1</Tabs.Tab>
   *   <Tabs.Tab id="tab2">Tab 2</Tabs.Tab>
   *   <Tabs.Tab id="tab3">Tab 3</Tabs.Tab>
   * </Tabs.Root>
   */
  Root: React.FC<TabsRootProps>;
  /**
   * The `id` is used to identify the tab and is required
   * if you want to use the `selectedKey` prop on the `Root` component.
   *
   * @example
   * <Tabs.Tab id="explore" tx="Generic.explore" /> // Uses a translation key
   * <Tabs.Tab id="admin">Admin</Tabs.Tab>
   */
  Tab: React.FC<TabProps>;
}

/**
 * Subnavigation tabs.
 *
 * This is made with the [Tabs](https://design-system.cfahome.com/components/tabs/react) component from `@cfa/react-core`.
 *
 * This sits below the header and is used to
 * navigate between different sections of the page.
 *
 * @example
 *  <Tabs.Root
 *    selectedKey={selectedTab}
 *    onSelectionChange={setSelectedTab}
 *  >
 *    <Tabs.Tab id="explore" tx="Generic.explore" />
 *    <Tabs.Tab id="admin" tx="Generic.admin" />
 *  </Tabs.Root>
 */
export const Tabs: Tabs = {
  Root: TabsRoot,
  Tab,
};

interface Header {
  /**
   * The root component.
   *
   * This serves as the parent component to compose the header.
   *
   * From the `md` breakpoint and down, the main header
   * will be hidden and only the tabs component will be rendered, if given one.
   *
   * @example
   * <Header.Root>
   *   <Header.Title>Title</Header.Title>
   * </Header.Root>
   */
  Root: React.FC<RootProps>;
  /**
   * This is the text that is centered in the header.
   *
   * @example
   * <Header.Title>{someComputedText}</Header.Title>
   * <Header.Title tx="Generic.explore" /> // Uses a translation key
   */
  Title: React.FC<TitleProps>;
  /**
   * This is the button that is used to navigate back.
   * It is placed on the left side of the header.
   *
   * @example
   * <Header.Back to={'/'}>{someComputedText}</Header.Back>
   * <Header.Back to={'/'} tx="Generic.explore" /> // Uses a translation key
   * // use as="button" to render a button instead of a link
   * <Header.Back as="button" onClick={() => navigate(-1)}>Some Back Text</Header.Back>
   */
  Back: typeof Back;
  /**
   * Right side action icon buttons.
   *
   * @example
   * <Header.ActionIconButton icon={<IconUser />} onClick={() => navigate('/settings')} />
   * <Header.ActionIconButton icon={<IconPrint />} text="Print" onClick={() => print()} />
   * <Header.ActionIconButton icon={<IconUser />} as="link" to={routes.plans.managedPlansEdit({ planId })} />
   */
  ActionIconButton: typeof ActionIconButton;
}

/**
 * Composable header.
 *
 * @since November 2025
 *
 * @example
 * <Header.Root>
 *   <Header.Title>Some Title</Header.Title>
 *   <Header.Back as="button" onClick={() => navigate(-1)}>Some Back Text</Header.Back>
 *   <Header.ActionIconButton icon={<IconBell />} />
 *   <Header.ActionIconButton icon={<IconUser />} />
 * </Header.Root> // Renders a header with the title, back button, and action icon buttons.
 *
 * @example
 * // with subnavigation tabs
 * <Header.Root className="mb-8">
 *   <Header.Title tx="Generic.explore" />
 *   <Tabs.Root selectedKey={selectedTab} onSelectionChange={setSelectedTab}>
 *     <Tabs.Tab id="explore" tx="Generic.explore" />
 *     <Tabs.Tab id="admin" tx="Generic.admin" />
 *   </Tabs.Root>
 * </Header.Root>
 */
export const Header: Header = {
  Root,
  Title,
  Back,
  ActionIconButton,
};
